import { type CSSProperties, type FC, type PropsWithChildren, type ReactNode, useCallback, useMemo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Heading } from "@stubhub/broadway-web";
import type { FlagCountry } from "@stubhub/broadway-web/icons/flags";
import {
  EventGridListItem,
  type EventGridListItemEventState,
  EventGridListItemEventType,
  EventGridListItemKind,
  EventGridListItemVariant,
} from "@stubhub/ensemble-discovery";
import type { EventListGridItem } from "src/http/types";
import { Content } from "src/shared/components/content";
import { useInView } from "src/shared/hooks/useInView";
import { DAY_OF_MONTH_TBA } from "./constants";
import { CALENDAR_CSS_VARIABLES, calendarTile } from "./styles";

// Invisible sentinel for in-view tracking — placed inside the tile so an
// IntersectionObserver fires when the tile scrolls into the viewport.
const inViewSentinel = css({
  width: "primitives.0",
  height: "primitives.0",
  position: "absolute",
});

// Wrapper element holds the inline `--component-primary-color` CSS variable
// because EventGridListItem's Props omit `style`. The variable cascades to
// the EventGridListItem container and its children (topLine, hover borders).
const tileWrapper = css({
  display: "contents",
});

// EventGridListItem's recipe container overrides (layout/padding resets, hover
// treatment) are now folded into the calendarTile SVA's base `root` slot so a
// single class is used in `classes.container` without needing `cx()`.

interface Props {
  /** URL to navigate to when the card is clicked (used when `eventState === Active`) */
  eventURL?: string;
  /** Day number to display in the card header (1-31, "TBA" if not announced, null for empty days) */
  dayOfMonth: number | typeof DAY_OF_MONTH_TBA | null;
  /** Heading content to display in the card's top line, alongside the day of month */
  heading?: ReactNode;
  /** Whether to render a borderless heading */
  borderlessHeading?: boolean;
  /** Call-to-action content to display in the card footer */
  cta?: ReactNode;
  /** Primary color for styling the card's theme */
  primaryColor?: CSSProperties["color"];
  /** Whether this card represents today's date */
  today?: boolean;
  /** Click event handler (called with `null` for parity with the legacy API) */
  onClick?: (event: null) => void;
  /** Mouse enter event handler (called with `null` for parity with the legacy API) */
  onMouseEnter?: (event: null) => void;
  /** Fired once when the tile first scrolls into view (75% visible) */
  onInView?: (() => void) | undefined;
  /** Accessibility label for screen readers */
  ariaLabel?: string | undefined;
  /**
   * Event for EventGridListItem context. Required alongside `eventState` so
   * Ensemble children (e.g. EventGridListItemTags) can read context for tooltips
   * and qualifier rendering. Omit (alongside `eventState`) for empty days and
   * multi-event parent tiles, which render as plain `<div>`.
   */
  event?: EventListGridItem | undefined;
  /**
   * Drives interactivity:
   * - `Active` → Ensemble renders an `<a>` tile.
   * - `SoldOut` → Ensemble renders a `<button>` tile.
   * - omitted → static `<div>` (multi-event parent or empty).
   */
  eventState?: EventGridListItemEventState | undefined;
}

/**
 * Generic base component for rendering a calendar date card that can be reused across different calendar views.
 *
 * Single-event tiles (caller passes `event` + `eventState`) render via Ensemble's
 * `EventGridListItem` so their children (merch tags, ctas) can consume
 * `useEventGridListItemContext()` for tooltip + qualifier rendering. Empty days
 * and multi-event parent tiles render as plain `<div>` since no Ensemble
 * children are mounted on those.
 */
export const BaseDateCard: FC<PropsWithChildren<Props>> = ({
  children,
  eventURL,
  dayOfMonth,
  heading,
  borderlessHeading = false,
  cta,
  primaryColor,
  today = false,
  onClick,
  onMouseEnter,
  onInView,
  ariaLabel,
  event,
  eventState,
}) => {
  const isDayOfMonthTba = dayOfMonth === DAY_OF_MONTH_TBA;
  const isEmpty = !children;
  const hasPrimaryColor = !!primaryColor;
  // Caller passes both `event` and `eventState` for interactive single-event tiles.
  // Multi-event parents and empty days omit them and render as a static <div>.
  const isInteractive = !isEmpty && event !== undefined && eventState !== undefined;
  const mode = isEmpty ? "empty" : isInteractive ? "interactive" : "static";

  // Slot recipes type slot classes as `string | undefined`; every slot here
  // has a base style so we coerce to `string` with `?? ""` at each callsite.
  const rawTileStyles = useMemo(
    () =>
      calendarTile({
        mode,
        primaryColorState: hasPrimaryColor ? "applied" : "none",
        todayHighlight: today ? "today" : "default",
        dayOfMonthState: isDayOfMonthTba ? "tba" : "normal",
        headingBorder: borderlessHeading ? "borderless" : "bordered",
      }),
    [mode, hasPrimaryColor, today, isDayOfMonthTba, borderlessHeading],
  );
  const tileStyles = useMemo(
    () => ({
      root: rawTileStyles.root ?? "",
      topLine: rawTileStyles.topLine ?? "",
      body: rawTileStyles.body ?? "",
      cta: rawTileStyles.cta ?? "",
      dayOfMonth: rawTileStyles.dayOfMonth ?? "",
    }),
    [rawTileStyles],
  );

  const containerStyle: CSSProperties = useMemo(
    () => ({
      [CALENDAR_CSS_VARIABLES.primaryColor]: primaryColor ?? "transparent",
    }),
    [primaryColor],
  );

  const headingBlock = useMemo(
    () => (
      <>
        <Heading variant="title" size="small" as="h3" className={tileStyles.dayOfMonth}>
          {isDayOfMonthTba ? /* "TBA" */ <Content id="cid$#3860643#" /> : dayOfMonth}
        </Heading>
        {heading}
      </>
    ),
    [tileStyles.dayOfMonth, isDayOfMonthTba, dayOfMonth, heading],
  );

  const { ref: inViewRef } = useInView({
    triggerOnce: true,
    threshold: 0.75,
    onChange: (inView: boolean) => {
      if (inView) onInView?.();
    },
  });

  const contentBlock = useMemo(
    () => (
      <>
        <div className={tileStyles.topLine}>{headingBlock}</div>
        <div className={tileStyles.body}>{children}</div>
        {!!cta && <div className={tileStyles.cta}>{cta}</div>}
        {!!onInView && <div className={inViewSentinel} ref={inViewRef} aria-hidden />}
      </>
    ),
    [tileStyles.topLine, tileStyles.body, tileStyles.cta, headingBlock, children, cta, onInView, inViewRef],
  );

  const handleClick = useCallback(() => {
    onClick?.(null);
  }, [onClick]);

  const handleMouseEnter = useCallback(() => {
    onMouseEnter?.(null);
  }, [onMouseEnter]);

  if (isEmpty) {
    return (
      <div className={tileStyles.root} style={containerStyle} aria-hidden>
        <div className={tileStyles.topLine}>{headingBlock}</div>
      </div>
    );
  }

  if (event !== undefined && eventState !== undefined) {
    // Use Ensemble's EventGridListItem as the root anchor so children can read
    // `useEventGridListItemContext()` for tag tooltip + qualifier rendering.
    // Ensemble picks the element type from `eventState`: Active → <a>, otherwise <button>.
    return (
      // The wrapping span (display: contents) holds the CSS variable for primaryColor
      // because EventGridListItem's Props explicitly omit `style`. With display: contents,
      // the wrapper is structurally invisible — EventGridListItem remains the grid item.
      <span className={tileWrapper} style={containerStyle}>
        <EventGridListItem
          href={eventURL ?? "#"}
          target="_blank"
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          kind={EventGridListItemKind.Neutral}
          variant={EventGridListItemVariant.Ghost}
          eventState={eventState}
          eventType={event.isParkingEvent ? EventGridListItemEventType.Parking : EventGridListItemEventType.Event}
          eventTitle={event.name}
          venueName={event.venueName ?? ""}
          venueLocation={[event.venueCity, event.venueStateProvince].filter(Boolean).join(", ")}
          venueCountryCode={(event.countryCode ?? "") as FlagCountry}
          startTimeMillis={event.eventMetadata?.common?.eventStartDateTime}
          endTimeMillis={event.eventMetadata?.common?.eventEndDateTime ?? undefined}
          venueTimeZoneOffsetMillis={event.eventMetadata?.common?.venueTimeZoneOffset}
          passLength={event.eventMetadata?.common?.passLength ?? undefined}
          isDateConfirmed={event.isDateConfirmed}
          isTimeConfirmed={event.isTimeConfirmed}
          classes={{ container: tileStyles.root }}
          aria-label={ariaLabel}
        >
          {contentBlock}
        </EventGridListItem>
      </span>
    );
  }

  // Fallback path: static <div> (multi-event parent — no event/eventState provided).
  return (
    <div className={tileStyles.root} style={containerStyle} aria-label={ariaLabel}>
      {contentBlock}
    </div>
  );
};
